import { ReactNode, useEffect, useMemo, useRef } from 'react'

import { marketBasket, marketBasketSettings } from '@/mocks'
import { useQuery } from '@api/hooks'
import { MarketBasketStatus, MarketBasketWithSettings } from '@domain'
import { ActionCenterKeys } from '@services/actionCenterTasks.api'
import { DirectDealKeys } from '@services/directDeals.api'
import MarketBasketsApi, { MarketBasketsKeys } from '@services/marketBaskets.api'
import { useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { MarketBasketContext, MarketBasketContextType } from './MarketBaskets.hooks'

export enum PurchaseGroupMatchType { // FIXME: rename this to MatchType
	SIMILAR_MATCH = 'SIMILAR_MATCH',
	EXACT = 'EXACT',
	NO_MATCH = 'NO_MATCH',
}

export default function MarketBasketProvider({ children }: { children: ReactNode }) {
	const { marketBasketId } = useParams()
	const queryClient = useQueryClient()
	const { data: marketBasket, isFetching: isRefreshing } = useQuery({
		queryKey: MarketBasketsKeys.marketBasket(marketBasketId),
		queryFn: () => MarketBasketsApi.marketBasket(marketBasketId),
		refetchInterval(query) {
			return query.state.data?.status === MarketBasketStatus.RECALCULATING ||
				query.state.data?.status === MarketBasketStatus.PROCESSING
				? 5000
				: false
		},
	})

	// Ref to handle invalidation of queries when the market basket status changes from recalculating/processing to analyzed/completed
	const previousStatusRef = useRef<string | null>(null)

	useEffect(() => {
		previousStatusRef.current = null
	}, [marketBasketId])

	useEffect(() => {
		if (!marketBasketId || !marketBasket?.status) return

		if (previousStatusRef.current == null) {
			previousStatusRef.current = marketBasket.status
			return
		}

		const previousStatus = previousStatusRef.current
		const currentStatus = marketBasket.status

		const wasRecalculatingOrProcessing =
			previousStatus === MarketBasketStatus.RECALCULATING ||
			previousStatus === MarketBasketStatus.PROCESSING

		const isAnalyzedOrCompleted =
			currentStatus === MarketBasketStatus.ANALYZED ||
			currentStatus === MarketBasketStatus.COMPLETED

		if (wasRecalculatingOrProcessing && isAnalyzedOrCompleted) {
			queryClient.removeQueries({
				queryKey: DirectDealKeys.all(marketBasketId),
			})
			queryClient.invalidateQueries({
				queryKey: DirectDealKeys.summary(marketBasketId),
			})
			queryClient.invalidateQueries({
				queryKey: ActionCenterKeys.tasks(marketBasketId),
			})
		}

		previousStatusRef.current = currentStatus
	}, [marketBasket?.status, marketBasketId, queryClient])

	const {
		data: settings,
		isLoading: isLoadingSettings,
		isFetching: isRefreshingSettings,
	} = useQuery({
		queryKey: MarketBasketsKeys.settings(marketBasketId),
		queryFn: () => MarketBasketsApi.settings(marketBasketId),
		enabled: !!marketBasketId,
	})

	const value = useMemo(() => {
		if (!marketBasket) {
			return {
				marketBasket: null,
				isLoading: true,
				isRefreshing: false,
				isAnalysisComplete: false,
				isPalantirBasket: false,
			}
		}

		const marketBasketWithSettings = {
			...marketBasket,
			settings,
		}

		const completed =
			marketBasketWithSettings.status === 'COMPLETED' ||
			marketBasketWithSettings.status === 'ANALYZED'

		return {
			marketBasket: marketBasketWithSettings,
			isLoading: isLoadingSettings,
			isRefreshing: isRefreshing || isRefreshingSettings,
			isAnalysisComplete: completed,
			isPalantirBasket: marketBasketWithSettings.sourceType === 'PALANTIR',
		}
	}, [isLoadingSettings, isRefreshing, isRefreshingSettings, marketBasket, settings])

	return <MarketBasketContext.Provider value={value}>{children}</MarketBasketContext.Provider>
}

export function MockedMarketBasketProvider({
	children,
	override = null,
}: {
	children: ReactNode
	override?: MarketBasketContextType
}) {
	const value = useMemo<MarketBasketContextType>(
		() =>
			override ??
			({
				marketBasket: {
					...marketBasket,
					status: marketBasket.status as MarketBasketStatus,
					settings: marketBasketSettings,
				} as MarketBasketWithSettings,
				isLoading: false,
				isRefreshing: false,
				isAnalysisComplete: true,
				isPalantirBasket: false,
			} as MarketBasketContextType),
		[override]
	)
	return <MarketBasketContext.Provider value={value}>{children}</MarketBasketContext.Provider>
}
